// ── Shared tag chip-input + typeahead dropdown (C1511) ──
//
// Two hosts share this: the New Task modal (pages/board.js, allowCreate:false — existing
// tags only, so the finalize PATCH can never 400 on TAGS_UNREGISTERED again) and the
// task-detail panel/page (components/task-detail.js, allowCreate:true — keeps the
// register-a-new-tag-with-a-description flow that already worked there, just relocated).
//
// Modelled on components/member-combo.js, not on the older `.task-detail-tag-suggestions`
// pattern — member-combo is the only existing dropdown in this SPA that works INSIDE a
// modal: its dropdown is body-appended + `position: fixed`, so `.modal`'s
// `overflow-y:auto; max-height:85vh` (and the panel's own `.task-detail-body` scroller)
// can never clip it. Same discipline throughout: mousedown+preventDefault on the dropdown
// (never lets a click blur the input first), a guarded blur timeout, capture-phase
// scroll/resize repositioning, and an Escape handler that stopPropagation()s while the
// dropdown is open — ui.js's openModal() (and task-detail's own escHandler) register a
// bubble-phase document `Escape -> close` that would otherwise eat the first Escape meant
// only for this dropdown.

import { api } from '../api.js';
import { escapeHtml } from '../utils.js';
import { loadTags, invalidateTags } from './tags.js';

// escapeHtml() escapes & < > via textContent->innerHTML but not '"' — needed here for the
// chip `title="<description>"` attribute (same gap member-combo.js's escapeAttr() covers).
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

// Pure — which of `tags` ({name, description, ...}) match `query`, excluding names already
// in `selected`, capped at `limit`. Case-insensitive substring match against `name` OR
// `description` (C1518 — every tag has a required description per C1038, so this lets a user
// find a tag by what it's for, not just its exact slug). `description` may be null/absent
// (defensive against older rows/API shapes) — never assumed truthy. Name matches are ranked
// before description-only matches (both groups keep the API's `ORDER BY name`), so a broad
// word hit across many descriptions can't push an exact name match past `limit`. Empty query
// returns the unselected pool unfiltered (so focusing the field with nothing typed still
// browses the full registry, same as member-combo's own empty-query behavior).
export function filterTagSuggestions(tags, query, selected = [], limit = 8) {
  const q = String(query || '').trim().toLowerCase();
  const selectedSet = new Set(selected);
  const pool = (tags || []).filter(t => t && t.name && !selectedSet.has(t.name));
  if (!q) return pool.slice(0, limit);

  const nameMatches = [];
  const descMatches = [];
  for (const t of pool) {
    if (t.name.toLowerCase().includes(q)) nameMatches.push(t);
    else if (String(t.description || '').toLowerCase().includes(q)) descMatches.push(t);
  }
  return [...nameMatches, ...descMatches].slice(0, limit);
}

// Pure — splits a pasted "a, b, c" into trimmed, deduped, non-empty names, order preserved.
export function parseTagInput(raw) {
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  for (const part of String(raw).split(',')) {
    const name = part.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

const EMPTY_ALLOW_CREATE_MSG = 'No tags yet — type a name to create one';
const EMPTY_FIXED_MSG = 'No matching tags — add new tags from the task’s detail panel';

// attachTagTypeahead(input, opts) — replaces `input` (a plain <input type="text">, already
// in the DOM) with a `.tag-typeahead` chip row wrapping it, and wires the whole
// chip-add/remove + typeahead-dropdown behavior onto it.
//
// opts:
//   projectId      number|string  required — scopes loadTags()/POST /tags
//   initialTags    string[]       chips to seed (default [])
//   scrollEl       Element|null   scroll container to reposition the dropdown against
//   allowCreate    boolean        false: existing tags only, unmatched query -> empty
//                                 message, Enter/click never creates. true: an unmatched
//                                 query offers a "Create: <name>" row -> inline
//                                 required-description form -> POST /tags -> chip added.
//   onChange(names)               fired after the chip set changes from a user action
//                                 (add/remove) — never on setTags(), which is host-driven.
//   isAlive()      boolean        false once the host UI is gone (guards the blur timer)
//   onlyTags()     string[]|null  optional: the registry names the dropdown may offer (the
//                                 bulk toolbar's Remove tag offers only tags the selected
//                                 tasks carry, TPT9098); null/absent offers the whole registry
//   emptyMessage   string         optional: replaces the no-matching-tags text
//
// Returns { getTags, setTags, refresh, close, destroy }.
export function attachTagTypeahead(input, opts = {}) {
  const noop = { getTags: () => [], setTags() {}, refresh() {}, close() {}, destroy() {} };
  if (!input) return noop;

  const { projectId, initialTags = [], scrollEl = null, allowCreate = false, onChange } = opts;
  const isAliveFn = typeof opts.isAlive === 'function' ? opts.isAlive : () => true;
  const onlyTagsFn = typeof opts.onlyTags === 'function' ? opts.onlyTags : null;
  const emptyMsg = opts.emptyMessage || (allowCreate ? EMPTY_ALLOW_CREATE_MSG : EMPTY_FIXED_MSG);

  const wrapper = document.createElement('div');
  wrapper.className = 'tag-typeahead';
  input.replaceWith(wrapper);

  const chipsHost = document.createElement('span');
  chipsHost.className = 'tag-typeahead-chips';
  wrapper.appendChild(chipsHost);
  wrapper.appendChild(input);
  input.classList.add('tag-typeahead-input');
  input.type = 'text';
  input.autocomplete = 'off';
  if (!input.placeholder) input.placeholder = 'Add tag...';

  let tags = [...new Set((initialTags || []).filter(Boolean))];
  let tagList = [];               // [{name, description, ...}] from loadTags()
  let tagMap = new Map();         // name -> description, for chip titles
  let dropdown = null;
  let options = [];               // current dropdown rows: {type:'tag'|'create', name, description?}
  let activeIndex = -1;
  let blurTimer = null;
  // True while the create-with-description sub-form (startCreate(), below) is showing.
  // startCreate() moves focus from `input` into that form's own .tag-create-desc input —
  // a JS-triggered focus() elsewhere always blurs whatever was focused, so `input`'s own
  // blur fires here even though the user never left the widget. Without this guard the
  // blur handler's 150ms timer would hideDropdown() (removing the dropdown node) out from
  // under the very form the user is still typing a description into. Same guard shape as
  // embed-menu.js's/board.js's `pickerOpen` flag for the analogous native-file-dialog case.
  let creatingTag = false;

  function fireChange() {
    if (typeof onChange === 'function') onChange(tags.slice());
  }

  function renderChips() {
    chipsHost.innerHTML = tags.map(name => {
      const desc = tagMap.get(name);
      return `<span class="tag tag-typeahead-chip"${desc ? ` title="${escapeAttr(desc)}"` : ''}>${escapeHtml(name)}<span class="tag-typeahead-chip-remove" data-tag="${escapeAttr(name)}">&times;</span></span>`;
    }).join('');
  }

  chipsHost.addEventListener('mousedown', (e) => {
    const btn = e.target.closest('.tag-typeahead-chip-remove');
    if (!btn) return;
    e.preventDefault(); // keep focus on the input — don't let the click blur it first
    tags = tags.filter(t => t !== btn.dataset.tag);
    renderChips();
    fireChange();
    if (dropdown) renderDropdown();
  });

  function positionDropdown() {
    if (!dropdown) return;
    // Positioned off the whole wrapper (not just `input`) — chips can wrap the row to
    // multiple lines, and the dropdown must sit below all of them, not below wherever
    // the input itself happens to have landed.
    const r = wrapper.getBoundingClientRect();
    dropdown.style.top = `${r.bottom + 2}px`;
    dropdown.style.left = `${r.left}px`;
    dropdown.style.width = `${Math.max(r.width, 220)}px`;
  }

  const onScrollOrResize = () => positionDropdown();
  if (scrollEl) scrollEl.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  function buildOptions(query) {
    const q = query.trim();
    const offered = onlyTagsFn ? onlyTagsFn() : null;
    const pool = Array.isArray(offered) ? tagList.filter(t => offered.includes(t.name)) : tagList;
    const matches = filterTagSuggestions(pool, q, tags, 8)
      .map(t => ({ type: 'tag', name: t.name, description: t.description }));
    if (!allowCreate || !q) return matches;
    const qLower = q.toLowerCase();
    const existsExact = tagList.some(t => t.name.toLowerCase() === qLower);
    const alreadySelected = tags.some(n => n.toLowerCase() === qLower);
    if (existsExact || alreadySelected) return matches;
    return [{ type: 'create', name: q }, ...matches];
  }

  function renderOptionRow(o, i) {
    if (o.type === 'create') {
      return `<div class="tag-typeahead-option tag-typeahead-option-create" data-index="${i}">Create: <strong>${escapeHtml(o.name)}</strong></div>`;
    }
    return `<div class="tag-typeahead-option" data-index="${i}">${escapeHtml(o.name)}${o.description ? `<small>${escapeHtml(o.description)}</small>` : ''}</div>`;
  }

  function updateActive() {
    if (!dropdown) return;
    dropdown.querySelectorAll('.tag-typeahead-option').forEach((el, i) => {
      el.classList.toggle('active', i === activeIndex);
    });
  }

  function renderDropdown() {
    if (!dropdown) {
      dropdown = document.createElement('div');
      dropdown.className = 'tag-typeahead-dropdown';
      dropdown.addEventListener('mousedown', onDropdownMousedown);
      document.body.appendChild(dropdown);
    }
    options = buildOptions(input.value);
    activeIndex = options.length ? 0 : -1;
    dropdown.innerHTML = options.length
      ? options.map(renderOptionRow).join('')
      : `<div class="tag-typeahead-empty">${escapeHtml(emptyMsg)}</div>`;
    updateActive();
    positionDropdown();
  }

  function hideDropdown() {
    if (dropdown) { dropdown.remove(); dropdown = null; }
    options = [];
    activeIndex = -1;
  }

  function openDropdown() {
    renderDropdown();
  }

  function commitTag(name) {
    creatingTag = false; // covers the confirmCreate() success path below, which routes through here
    if (!tags.includes(name)) {
      tags.push(name);
      fireChange();
    }
    input.value = '';
    renderChips();
    renderDropdown(); // stay open, refreshed (excludes the tag just added) — continuous multi-add
  }

  // startCreate(name) — inline required-description form, same shape/behavior as the
  // pre-C1511 task-detail.js showNewTagDescForm(), just targeting this module's dropdown.
  function startCreate(name) {
    if (!dropdown) return;
    creatingTag = true;
    dropdown.innerHTML = `
      <div class="tag-create-form">
        <span class="tag-create-name">${escapeHtml(name)}</span>
        <input type="text" class="tag-create-desc" placeholder="Description (required)" autocomplete="off">
        <button type="button" class="tag-create-confirm btn btn-sm btn-primary" disabled>Add</button>
        <button type="button" class="tag-create-cancel btn btn-sm btn-ghost">Cancel</button>
        <span class="tag-create-error" style="display:none"></span>
      </div>`;
    const descInput = dropdown.querySelector('.tag-create-desc');
    const confirmBtn = dropdown.querySelector('.tag-create-confirm');
    const cancelBtn = dropdown.querySelector('.tag-create-cancel');
    const errorEl = dropdown.querySelector('.tag-create-error');
    descInput.focus();
    descInput.addEventListener('input', () => { confirmBtn.disabled = !descInput.value.trim(); });
    cancelBtn.addEventListener('mousedown', (e) => { e.preventDefault(); creatingTag = false; renderDropdown(); input.focus(); });

    async function confirmCreate() {
      const description = descInput.value.trim();
      if (!description) return;
      confirmBtn.disabled = true;
      confirmBtn.textContent = '...';
      errorEl.style.display = 'none';
      try {
        await api.post(`/projects/${projectId}/tags`, { tags: [{ name, description }] });
        invalidateTags(projectId);
        tagList = await loadTags(projectId);
        tagMap = new Map(tagList.map(t => [t.name, t.description]));
        commitTag(name);
        input.focus();
      } catch (err) {
        errorEl.textContent = err.message || 'Failed to register tag';
        errorEl.style.display = 'block';
        confirmBtn.disabled = false;
        confirmBtn.textContent = 'Add';
      }
    }
    descInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); confirmCreate(); }
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); creatingTag = false; renderDropdown(); input.focus(); }
    });
    confirmBtn.addEventListener('mousedown', (e) => { e.preventDefault(); confirmCreate(); });
  }

  function selectOption(o) {
    if (o.type === 'create') startCreate(o.name);
    else commitTag(o.name);
  }

  function onDropdownMousedown(e) {
    const opt = e.target.closest('.tag-typeahead-option');
    if (!opt) return;
    e.preventDefault(); // keep focus on the input — don't let the click blur it first
    const entry = options[Number(opt.dataset.index)];
    if (entry) selectOption(entry);
  }

  input.addEventListener('focus', () => {
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    openDropdown();
  });

  input.addEventListener('click', () => { if (!dropdown) openDropdown(); });

  input.addEventListener('input', () => { renderDropdown(); });

  input.addEventListener('paste', (e) => {
    const raw = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    if (!raw.includes(',')) return; // single value — let the default paste land, dropdown filters as usual
    e.preventDefault();
    const lowerToReal = new Map(tagList.map(t => [t.name.toLowerCase(), t.name]));
    let leftover = '';
    for (const name of parseTagInput(raw)) {
      const real = lowerToReal.get(name.toLowerCase());
      if (real) commitTag(real);
      else if (!leftover) leftover = name; // first unmatched name left for the user to resolve by hand
    }
    input.value = leftover;
    renderDropdown();
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!dropdown) { openDropdown(); return; }
      if (options.length) { activeIndex = (activeIndex + 1) % options.length; updateActive(); }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (dropdown && options.length) { activeIndex = (activeIndex - 1 + options.length) % options.length; updateActive(); }
    } else if (e.key === 'Enter' || e.key === ',') {
      // Always prevented — this field can sit inside a <form> (New Task modal), where a
      // bare Enter would submit it; ',' is a chip separator, never a literal character.
      e.preventDefault();
      if (dropdown && options.length && activeIndex >= 0) selectOption(options[activeIndex]);
    } else if (e.key === 'Backspace' && !input.value && tags.length) {
      tags.pop();
      renderChips();
      fireChange();
      if (dropdown) renderDropdown();
    } else if (e.key === 'Escape' && dropdown) {
      // Dropdown open: close it and stop the keypress from reaching a host-level Escape
      // handler (openModal()'s document listener, or task-detail's escHandler), which
      // would otherwise close the whole modal/panel instead of just this dropdown.
      e.preventDefault();
      e.stopPropagation();
      hideDropdown();
    }
    // Escape with no dropdown open: not handled here — bubbles to the host as normal.
  });

  input.addEventListener('blur', () => {
    if (creatingTag) return; // focus moved into the create-form's own input, not away from the widget
    blurTimer = setTimeout(() => {
      if (!isAliveFn()) return;
      hideDropdown();
      input.value = '';
    }, 150);
  });

  function getTags() { return tags.slice(); }

  function setTags(names) {
    tags = [...new Set((names || []).filter(Boolean))];
    renderChips();
  }

  function refresh() {
    renderChips();
    if (dropdown) renderDropdown();
  }

  function destroy() {
    hideDropdown();
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (scrollEl) scrollEl.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  }

  renderChips(); // initial paint — no descriptions yet, chip titles fill in once loadTags() resolves
  loadTags(projectId).then(list => {
    tagList = list;
    tagMap = new Map(list.map(t => [t.name, t.description]));
    renderChips();
    if (dropdown) renderDropdown();
  });

  return { getTags, setTags, refresh, close: hideDropdown, destroy };
}
